import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/apiConfig';
import SearchBar from './ui/SearchBar';
import AirDatePicker from './ui/AirDatePicker';
import Swal from 'sweetalert2';

const SuperAdminDashboardTables = () => {
    const [companies, setCompanies] = useState([]);
    const [plans, setPlans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchExpired, setSearchExpired] = useState('');
    const [searchExpiring, setSearchExpiring] = useState('');
    const [searchRemaining, setSearchRemaining] = useState('');
    const [expiredPage, setExpiredPage] = useState(1);
    const [expiringPage, setExpiringPage] = useState(1);
    const [remainingPage, setRemainingPage] = useState(1);
    const itemsPerPage = 5;

    // (Modal and other states are unchanged...)
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);

    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [planStartDate, setPlanStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [isAssigning, setIsAssigning] = useState(false);

    // Fetch companies and plans
    const fetchData = async () => {
        try {
            setIsLoading(true);
            const token = sessionStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const [compRes, planRes, payRes] = await Promise.allSettled([
                axios.get(`${API_BASE_URL}/company?limit=1000`, { headers }),
                axios.get(`${API_BASE_URL}/plan?limit=1000`, { headers }),
                axios.get(`${API_BASE_URL}/plan-payment?status=Verified&limit=1000`, { headers })
            ]);

            const fetchedCompanies = compRes.status === 'fulfilled' ? (compRes.value.data?.companies || []) : [];
            const fetchedPlans = planRes.status === 'fulfilled' ? (planRes.value.data?.plans || planRes.value.data || []) : [];
            const fetchedPayments = payRes.status === 'fulfilled' ? (payRes.value.data?.payments || []) : [];

            let hasUnexpectedError = false;

            if (compRes.status === 'rejected') {
                console.error("Error loading companies:", compRes.reason);
                if (compRes.reason?.response?.status !== 403) hasUnexpectedError = true;
            }
            if (planRes.status === 'rejected') {
                console.error("Error loading plans:", planRes.reason);
                if (planRes.reason?.response?.status !== 403) hasUnexpectedError = true;
            }
            if (payRes.status === 'rejected') {
                console.error("Error loading plan payments:", payRes.reason);
                if (payRes.reason?.response?.status !== 403) hasUnexpectedError = true;
            }

            if (hasUnexpectedError) {
                toast.error("Failed to load company and plan lists");
            }

            const companiesWithPayments = fetchedCompanies.map(comp => {
                const verifiedPayments = fetchedPayments.filter(p => String(p.companyId) === String(comp.id) && String(p.planId) === String(comp.planId));
                const totalPaid = verifiedPayments.reduce((sum, p) => sum + Number(p.amountPaid || 0), 0);
                return { ...comp, totalPaid };
            });

            setCompanies(companiesWithPayments);
            setPlans(fetchedPlans);
        } catch (err) {
            console.error("Error in fetchData:", err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Split companies into Expired, Expiring Soon (<= 10 days), and Remaining Payments
    const { expiredCompanies, expiringSoonCompanies, remainingPaymentCompanies } = useMemo(() => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        const expired = [];
        const expiringSoon = [];
        const remainingPayment = [];

        companies.forEach(company => {
            let currentDaysRemaining = null;
            if (company.planExpiryDate) {
                const tempExpiry = new Date(company.planExpiryDate);
                tempExpiry.setHours(0, 0, 0, 0);
                const tempDiffTime = tempExpiry.getTime() - now.getTime();
                currentDaysRemaining = Math.ceil(tempDiffTime / (1000 * 60 * 60 * 24));
            }

            // Check remaining payment first
            if (company.planId && company.finalPrice !== undefined) {
                const finalP = Number(company.finalPrice) || 0;
                const paid = Number(company.totalPaid) || 0;
                if (paid < finalP) {
                    remainingPayment.push({
                        ...company,
                        amountDue: finalP - paid,
                        totalPaid: paid,
                        daysRemaining: currentDaysRemaining
                    });
                }
            }

            if (!company.planExpiryDate) {
                expired.push(company);
                return;
            }

            const expiry = new Date(company.planExpiryDate);
            expiry.setHours(0, 0, 0, 0);

            if (expiry <= now) {
                expired.push(company);
            } else {
                const diffTime = expiry.getTime() - now.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                if (diffDays <= 10) {
                    expiringSoon.push({ ...company, daysRemaining: diffDays });
                }
            }
        });

        return { expiredCompanies: expired, expiringSoonCompanies: expiringSoon, remainingPaymentCompanies: remainingPayment };
    }, [companies]);

    // Expired Table Filtering
    const filteredExpired = useMemo(() => {
        return expiredCompanies.filter(c =>
            (c.companyName || '').toLowerCase().includes(searchExpired.toLowerCase())
        );
    }, [expiredCompanies, searchExpired]);

    // Expiring Table Filtering
    const filteredExpiring = useMemo(() => {
        return expiringSoonCompanies.filter(c =>
            (c.companyName || '').toLowerCase().includes(searchExpiring.toLowerCase())
        );
    }, [expiringSoonCompanies, searchExpiring]);

    // Remaining Table Filtering
    const filteredRemaining = useMemo(() => {
        return remainingPaymentCompanies.filter(c =>
            (c.companyName || '').toLowerCase().includes(searchRemaining.toLowerCase())
        );
    }, [remainingPaymentCompanies, searchRemaining]);

    // Pagination Slices
    const expiredTotalPages = Math.ceil(filteredExpired.length / itemsPerPage) || 1;
    const currentExpired = filteredExpired.slice((expiredPage - 1) * itemsPerPage, expiredPage * itemsPerPage);

    const expiringTotalPages = Math.ceil(filteredExpiring.length / itemsPerPage) || 1;
    const currentExpiring = filteredExpiring.slice((expiringPage - 1) * itemsPerPage, expiringPage * itemsPerPage);

    const remainingTotalPages = Math.ceil(filteredRemaining.length / itemsPerPage) || 1;
    const currentRemaining = filteredRemaining.slice((remainingPage - 1) * itemsPerPage, remainingPage * itemsPerPage);

    const handleOpenModal = (company) => {
        setSelectedCompany(company);
        setSelectedPlanId(company.planId || '');
        setPlanStartDate(new Date().toISOString().split('T')[0]);
        setCouponCode('');
        setAppliedCoupon(null);
        setIsModalOpen(true);
    };

    // Close Modal
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedCompany(null);
    };

    // Selected plan detailed metrics
    const planDetails = useMemo(() => {
        const plan = plans.find(p => String(p.id) === String(selectedPlanId));
        if (!plan) return null;

        const duration = Number(plan.planDurationDays || plan.duration_days || plan.durationDays) || 0;
        const price = Number(plan.planPrice || plan.plan_price) || 0;
        const discount = Number(plan.planDiscount || plan.plan_discount) || 0;
        const basePrice = price * (1 - discount / 100);

        // Expiry Date calculation
        let expiryDate = '';
        if (planStartDate && duration) {
            const dateObj = new Date(planStartDate);
            dateObj.setDate(dateObj.getDate() + duration);
            expiryDate = dateObj.toISOString().split('T')[0];
        }

        return { duration, price, discount, basePrice, expiryDate };
    }, [plans, selectedPlanId, planStartDate]);

    // Amount to pay calculation
    const amountToPay = useMemo(() => {
        if (!planDetails) return 0;
        const base = planDetails.basePrice;
        
        // Coupon discount
        let couponDiscountAmt = 0;
        if (appliedCoupon) {
            const val = Number(appliedCoupon.discountValue) || 0;
            if (appliedCoupon.discountType === 'percentage') {
                couponDiscountAmt = base * (val / 100);
            } else {
                couponDiscountAmt = val;
            }
        }

        return Math.max(0, base - couponDiscountAmt);
    }, [planDetails, appliedCoupon]);

    // Apply Coupon Code Action
    const handleApplyCoupon = async () => {
        if (!couponCode) {
            toast.error("Please enter a coupon code");
            return;
        }
        if (!selectedPlanId) {
            toast.error("Please select a plan first");
            return;
        }
        try {
            const token = sessionStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.post(`${API_BASE_URL}/coupon/validate`, {
                couponCode,
                planId: selectedPlanId,
                companyId: selectedCompany?.id
            }, { headers });

            if (response.data?.valid) {
                setAppliedCoupon(response.data.coupon);
                toast.success("Coupon applied successfully!");
            } else {
                toast.error(response.data?.message || "Invalid coupon");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Invalid coupon code");
        }
    };

    // Save Assign Plan Action
    const handleAssignPlan = async (e) => {
        e.preventDefault();
        if (!selectedPlanId) {
            toast.error("Please select a plan");
            return;
        }

        const conf = await Swal.fire({
            title: 'Save Plan?',
            text: 'Are you sure you want to save this plan?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#00334e',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Save',
            cancelButtonText: 'No, Cancel'
        });

        if (!conf.isConfirmed) return;

        try {
            setIsAssigning(true);
            const token = sessionStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const payload = {
                planId: selectedPlanId,
                planStartDate,
                planExpiryDate: planDetails?.expiryDate,
                couponCode: appliedCoupon ? appliedCoupon.couponCode : null
            };

            await axios.put(`${API_BASE_URL}/company/${selectedCompany.id}/assign-plan`, payload, { headers });
            
            toast.success("Plan updated successfully!");
            handleCloseModal();
            fetchData(); // Reload dashboard tables
        } catch (err) {
            console.error("Error assigning plan:", err);
            toast.error(err.response?.data?.message || "Failed to update plan");
        } finally {
            setIsAssigning(false);
        }
    };

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', margin: '40px 0' }}>
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <>
            <div className="dashboard-tables" style={{ marginTop: '16px' }}>
                {/* Expired Plans Table */}
                <div className="dashboard-table-container">
                    <div className="dashboard-table-header">
                        <h3 className="dashboard-table-title">Plan Expire Company List</h3>
                        <SearchBar
                            value={searchExpired}
                            onChange={(e) => { setSearchExpired(e.target.value); setExpiredPage(1); }}
                            placeholder="Search..."
                            label={null}
                            className="dashboard-header-search"
                        />
                    </div>
                    <div className="dashboard-table-wrapper">
                        <table className="dashboard-mini-table">
                            <thead>
                                <tr>
                                    <th>Company Name</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: 'right' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentExpired.length > 0 ? (
                                    currentExpired.map(comp => (
                                        <tr key={comp.id}>
                                            <td style={{ fontWeight: '500' }}>{comp.companyName}</td>
                                            <td>
                                                <span className="badge-critical" style={{
                                                    background: '#fee2e2',
                                                    color: '#991b1b',
                                                    padding: '3px 8px',
                                                    borderRadius: '12px',
                                                    fontSize: '11px',
                                                    fontWeight: '600'
                                                }}>Expired</span>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                    <button
                                                        type="button"
                                                        title="Send Expiry Alert on WhatsApp"
                                                        onClick={() => {
                                                            const mobile = comp.companyMobileNumber_1 || comp.companyMobileNumber_2 || comp.company_mobile_1 || comp.company_mobile_2;
                                                            if (!mobile) {
                                                                toast.warning('No mobile number available for this company');
                                                                return;
                                                            }
                                                            const cleanDigits = mobile.replace(/\D/g, '');
                                                            const phone = cleanDigits.length === 10 ? `91${cleanDigits}` : cleanDigits;
                                                            const companyName = comp.companyName || comp.company_name || 'Valued Customer';
                                                            const messageText = `Hello *${companyName}*,\n\nYour plan for *${companyName}* is expired. Please renew your plan.`;
                                                            const message = encodeURIComponent(messageText);
                                                            window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
                                                        }}
                                                        style={{
                                                            border: 'none',
                                                            background: 'none',
                                                            cursor: 'pointer',
                                                            padding: '2px',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            verticalAlign: 'middle'
                                                        }}
                                                    >
                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                                                            <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1"/>
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenModal(comp)}
                                                        className="btn-select-plan"
                                                        style={{
                                                            background: 'linear-gradient(135deg, #0f3a54 0%, #175475 100%)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            padding: '4px 10px',
                                                            fontSize: '12px',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                            transition: 'transform 0.2s, box-shadow 0.2s'
                                                        }}
                                                        onMouseOver={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                                                        onMouseOut={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                                                    >
                                                        Select Plan
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="3" className="empty-cell">No expired companies found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="dashboard-table-footer">
                        <span className="pagination-info">Page {expiredPage} of {expiredTotalPages}</span>
                        <div className="pagination-btns">
                            <button
                                onClick={() => setExpiredPage(p => Math.max(1, p - 1))}
                                disabled={expiredPage === 1}
                                className="pagination-btn"
                            >Prev</button>
                            <button
                                onClick={() => setExpiredPage(p => Math.min(expiredTotalPages, p + 1))}
                                disabled={expiredPage >= expiredTotalPages}
                                className="pagination-btn"
                            >Next</button>
                        </div>
                    </div>
                </div>

                {/* Expiring Remaining 10 Days Table */}
                <div className="dashboard-table-container">
                    <div className="dashboard-table-header">
                        <h3 className="dashboard-table-title">Plan Expiring (10 Days Left)</h3>
                        <SearchBar
                            value={searchExpiring}
                            onChange={(e) => { setSearchExpiring(e.target.value); setExpiringPage(1); }}
                            placeholder="Search..."
                            label={null}
                            className="dashboard-header-search"
                        />
                    </div>
                    <div className="dashboard-table-wrapper">
                        <table className="dashboard-mini-table">
                            <thead>
                                <tr>
                                    <th>Company Name</th>
                                    <th>Expiry Date</th>
                                    <th>Days Left</th>
                                    <th style={{ textAlign: 'right' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentExpiring.length > 0 ? (
                                    currentExpiring.map(comp => (
                                        <tr key={comp.id}>
                                            <td style={{ fontWeight: '500' }}>{comp.companyName}</td>
                                            <td>{comp.planExpiryDate ? new Date(comp.planExpiryDate).toLocaleDateString('en-GB') : 'N/A'}</td>
                                            <td>
                                                <span style={{
                                                    background: '#fef3c7',
                                                    color: '#92400e',
                                                    padding: '3px 8px',
                                                    borderRadius: '12px',
                                                    fontSize: '11px',
                                                    fontWeight: '600'
                                                }}>{comp.daysRemaining === 1 ? 'Last day' : `${comp.daysRemaining} days`}</span>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                 <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                     <button
                                                         type="button"
                                                         title="Send Expiry Alert on WhatsApp"
                                                         onClick={() => {
                                                             const mobile = comp.companyMobileNumber_1 || comp.companyMobileNumber_2 || comp.company_mobile_1 || comp.company_mobile_2;
                                                             if (!mobile) {
                                                                 toast.warning('No mobile number available for this company');
                                                                 return;
                                                             }
                                                             const cleanDigits = mobile.replace(/\D/g, '');
                                                             const phone = cleanDigits.length === 10 ? `91${cleanDigits}` : cleanDigits;
                                                             const companyName = comp.companyName || comp.company_name || 'Valued Customer';
                                                             const remainingDays = comp.daysRemaining !== undefined ? comp.daysRemaining : 10;
                                                             const remainingText = remainingDays === 1 ? '1 day' : `${remainingDays} days`;
                                                             const messageText = `Hello *${companyName}*,\n\nYour plan has *${remainingText}* remaining. After that, your plan will be deactivated. Please renew your plan.`;
                                                             const message = encodeURIComponent(messageText);
                                                             window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
                                                         }}
                                                         style={{
                                                             border: 'none',
                                                             background: 'none',
                                                             cursor: 'pointer',
                                                             padding: '2px',
                                                             display: 'inline-flex',
                                                             alignItems: 'center',
                                                             justifyContent: 'center',
                                                             verticalAlign: 'middle'
                                                         }}
                                                     >
                                                         <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                             <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                                                             <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1"/>
                                                         </svg>
                                                     </button>
                                                <button
                                                    onClick={() => handleOpenModal(comp)}
                                                    className="btn-select-plan"
                                                    style={{
                                                        background: 'linear-gradient(135deg, #0f3a54 0%, #175475 100%)',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        padding: '4px 10px',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                        transition: 'transform 0.2s, box-shadow 0.2s'
                                                    }}
                                                    onMouseOver={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                                                    onMouseOut={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                                                >
                                                    Select Plan
                                                </button>
                                            </div>
                                             </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="4" className="empty-cell">No expiring companies found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="dashboard-table-footer">
                        <span className="pagination-info">Page {expiringPage} of {expiringTotalPages}</span>
                        <div className="pagination-btns">
                            <button
                                onClick={() => setExpiringPage(p => Math.max(1, p - 1))}
                                disabled={expiringPage === 1}
                                className="pagination-btn"
                            >Prev</button>
                            <button
                                onClick={() => setExpiringPage(p => Math.min(expiringTotalPages, p + 1))}
                                disabled={expiringPage >= expiringTotalPages}
                                className="pagination-btn"
                            >Next</button>
                        </div>
                    </div>
                </div>

                {/* Remaining Payments Table */}
                <div className="dashboard-table-container" style={{ gridColumn: '1 / -1' }}>
                    <div className="dashboard-table-header">
                        <h3 className="dashboard-table-title">Remaining Payments</h3>
                        <SearchBar
                            value={searchRemaining}
                            onChange={(e) => { setSearchRemaining(e.target.value); setRemainingPage(1); }}
                            placeholder="Search..."
                            label={null}
                            className="dashboard-header-search"
                        />
                    </div>
                    <div className="dashboard-table-wrapper">
                        <table className="dashboard-mini-table">
                            <thead>
                                <tr>
                                    <th>Company Name</th>
                                    <th>Plan</th>
                                    <th>Expiry Date</th>
                                    <th>Final Price</th>
                                    <th>Paid So Far</th>
                                    <th>Amount Due</th>
                                    <th style={{ textAlign: 'right' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentRemaining.length > 0 ? (
                                    currentRemaining.map(comp => (
                                        <tr key={comp.id}>
                                            <td style={{ fontWeight: '500' }}>{comp.companyName}</td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span>{comp.planName || 'N/A'}</span>
                                                    {comp.daysRemaining !== null && (
                                                        <span style={{ 
                                                            color: '#64748b', 
                                                            fontSize: '11px',
                                                            background: '#f1f5f9',
                                                            padding: '2px 6px',
                                                            borderRadius: '10px',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {comp.daysRemaining < 0 ? `${Math.abs(comp.daysRemaining)} days overdue` : `${comp.daysRemaining} days left`}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>{comp.planExpiryDate ? new Date(comp.planExpiryDate).toLocaleDateString('en-GB') : 'N/A'}</td>
                                            <td>₹{comp.finalPrice}</td>
                                            <td style={{ color: '#059669', fontWeight: '500' }}>₹{comp.totalPaid}</td>
                                            <td>
                                                <span style={{
                                                    background: '#fee2e2',
                                                    color: '#b91c1c',
                                                    padding: '3px 8px',
                                                    borderRadius: '12px',
                                                    fontSize: '11px',
                                                    fontWeight: '600'
                                                }}>₹{comp.amountDue} Due</span>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <button
                                                    type="button"
                                                    title="Send Payment Alert on WhatsApp"
                                                    onClick={() => {
                                                        const mobile = comp.companyMobileNumber_1 || comp.companyMobileNumber_2 || comp.company_mobile_1 || comp.company_mobile_2;
                                                        if (!mobile) {
                                                            toast.warning('No mobile number available for this company');
                                                            return;
                                                        }
                                                        const cleanDigits = mobile.replace(/\D/g, '');
                                                        const phone = cleanDigits.length === 10 ? `91${cleanDigits}` : cleanDigits;
                                                        const companyName = comp.companyName || comp.company_name || 'Valued Customer';
                                                        const messageText = `Hello *${companyName}*,\n\nYour subscription payment is remaining. Out of *₹${comp.finalPrice}*, you have paid *₹${comp.totalPaid}*, and *₹${comp.amountDue}* is due. Please clear the remaining amount to avoid plan deactivation.`;
                                                        const message = encodeURIComponent(messageText);
                                                        window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
                                                    }}
                                                    style={{
                                                        border: 'none',
                                                        background: 'none',
                                                        cursor: 'pointer',
                                                        padding: '2px',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        verticalAlign: 'middle'
                                                     }}
                                                >
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                                                        <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1"/>
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="7" className="empty-cell">No companies with pending payments</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="dashboard-table-footer">
                        <span className="pagination-info">Page {remainingPage} of {remainingTotalPages}</span>
                        <div className="pagination-btns">
                            <button
                                onClick={() => setRemainingPage(p => Math.max(1, p - 1))}
                                disabled={remainingPage === 1}
                                className="pagination-btn"
                            >Prev</button>
                            <button
                                onClick={() => setRemainingPage(p => Math.min(remainingTotalPages, p + 1))}
                                disabled={remainingPage >= remainingTotalPages}
                                className="pagination-btn"
                            >Next</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Premium Plan Selector Modal */}
            {isModalOpen && (
                <div className="modal-overlay" style={{ animation: 'fadeIn 0.2s ease-out' }}>
                    <div className="modal-content" style={{ maxWidth: '850px', borderRadius: '16px', overflow: 'visible', background: 'white' }}>
                        <div className="modal-header" style={{ background: '#f8fafc', padding: '16px 24px', borderBottom: '1px solid #e2e8f0', borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }}>
                            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#0c3447' }}>Select Plan for {selectedCompany?.companyName}</h2>
                            <button onClick={handleCloseModal} className="close-btn" style={{ fontSize: '24px', color: '#94a3b8', background: 'transparent', border: 'none', cursor: 'pointer' }}>&times;</button>
                        </div>
                        <form onSubmit={handleAssignPlan}>
                            <div className="modal-body" style={{ padding: '24px', background: 'white' }}>
                                {/* Row 1 */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '20px' }}>
                                    <div className="form-group" style={{ margin: 0 }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Plan Name</label>
                                        <select
                                            value={selectedPlanId}
                                            onChange={(e) => { setSelectedPlanId(e.target.value); setAppliedCoupon(null); }}
                                            required
                                            style={{
                                                width: '100%',
                                                height: '42px',
                                                padding: '0 12px',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                color: '#1e293b',
                                                outline: 'none',
                                                background: 'white'
                                            }}
                                        >
                                            <option value="">-- Select Plan --</option>
                                            {plans.map(p => (
                                                <option key={p.id} value={p.id}>
                                                    {p.planName || p.plan_name} (₹{p.planPrice || p.plan_price})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group" style={{ margin: 0 }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Plan Start Date</label>
                                        <AirDatePicker
                                            value={planStartDate}
                                            onChange={(dateStr) => setPlanStartDate(dateStr)}
                                            required
                                            style={{
                                                width: '100%',
                                                height: '42px',
                                                padding: '0 12px',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                color: '#1e293b',
                                                outline: 'none'
                                            }}
                                        />
                                    </div>
                                    <div className="form-group" style={{ margin: 0 }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Plan Duration (Days)</label>
                                        <input
                                            type="text"
                                            readOnly
                                            disabled
                                            value={planDetails ? `${planDetails.duration} Days` : ''}
                                            placeholder="Auto-calculated from plan"
                                            style={{
                                                width: '100%',
                                                height: '42px',
                                                padding: '0 12px',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                background: '#f8fafc',
                                                color: '#64748b',
                                                outline: 'none'
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Row 2 */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '20px' }}>
                                    <div className="form-group" style={{ margin: 0 }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Plan Expiry Date</label>
                                        <input
                                            type="text"
                                            readOnly
                                            disabled
                                            value={planDetails?.expiryDate ? new Date(planDetails.expiryDate).toLocaleDateString('en-GB') : ''}
                                            placeholder="Auto-calculated (start date + plan days)"
                                            style={{
                                                width: '100%',
                                                height: '42px',
                                                padding: '0 12px',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                background: '#f8fafc',
                                                color: '#64748b',
                                                outline: 'none'
                                            }}
                                        />
                                    </div>
                                    <div className="form-group" style={{ margin: 0 }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Plan Price (₹)</label>
                                        <input
                                            type="text"
                                            readOnly
                                            disabled
                                            value={planDetails ? `₹${planDetails.price}` : '₹0'}
                                            style={{
                                                width: '100%',
                                                height: '42px',
                                                padding: '0 12px',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                background: '#f8fafc',
                                                color: '#64748b',
                                                outline: 'none'
                                            }}
                                        />
                                    </div>
                                    <div className="form-group" style={{ margin: 0 }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Discount (%)</label>
                                        <input
                                            type="text"
                                            readOnly
                                            disabled
                                            value={planDetails ? planDetails.discount : 0}
                                            style={{
                                                width: '100%',
                                                height: '42px',
                                                padding: '0 12px',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                background: '#f8fafc',
                                                color: '#64748b',
                                                outline: 'none'
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Row 3 */}
                                <div className="form-group" style={{ marginBottom: '24px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Amount to Pay (₹)</label>
                                    <div style={{
                                        background: '#f0fdf4',
                                        border: '1.5px solid #bbf7d0',
                                        borderRadius: '8px',
                                        padding: '12px 16px',
                                        fontSize: '18px',
                                        fontWeight: '700',
                                        color: '#15803d',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        minWidth: '150px',
                                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)'
                                    }}>
                                        ₹{amountToPay.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </div>
                                </div>

                                {/* Row 4 */}
                                <div className="form-group" style={{ margin: '0' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Apply Coupon Code</label>
                                    <div style={{ display: 'flex', gap: '12px', maxWidth: '400px' }}>
                                        <input
                                            type="text"
                                            placeholder="ENTER COUPON CODE"
                                            value={couponCode}
                                            onChange={(e) => setCouponCode(e.target.value)}
                                            style={{
                                                flex: '1',
                                                height: '42px',
                                                padding: '0 12px',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                color: '#1e293b',
                                                outline: 'none',
                                                textTransform: 'uppercase'
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleApplyCoupon}
                                            style={{
                                                background: '#0c3447',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                padding: '0 20px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                    {appliedCoupon && (
                                        <div style={{ color: '#16a34a', fontSize: '13px', fontWeight: '600', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            ✓ Coupon '{appliedCoupon.couponCode}' applied! (Discount: {appliedCoupon.discountType === 'percentage' ? `${appliedCoupon.discountValue}%` : `₹${appliedCoupon.discountValue}`})
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer" style={{ background: '#f8fafc', padding: '16px 24px', borderTop: '1px solid #e2e8f0', borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    style={{
                                        background: '#e2e8f0',
                                        color: '#475569',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '10px 20px',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isAssigning}
                                    style={{
                                        background: 'linear-gradient(135deg, #0c3447 0%, #175475 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '10px 24px',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 6px rgba(12, 52, 71, 0.2)'
                                    }}
                                >
                                    {isAssigning ? 'Saving...' : 'Save Plan'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default SuperAdminDashboardTables;
